import React from 'react';
import { ArrowLeft, Hash, User, Calendar, Clock } from 'lucide-react';
import { money, fmtDate } from './helpers';
import { StatusPill, MetaItem, OwnerValue } from './atoms';

export const ListingHero: React.FC<{
  entity: Record<string, any>;
  eid: number;
  name: string;
  status: string;
  isDeleted: boolean;
  coverImage?: string;
  primaryPrice: unknown;
  onBack: () => void;
}> = ({ entity: e, eid, name, status, isDeleted, coverImage, primaryPrice, onBack }) => (
  <>
    {/* Back */}
    <button
      onClick={onBack}
      className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800"
    >
      <ArrowLeft className="h-4 w-4" /> Back to listings
    </button>

    {/* Hero */}
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
      <div className="relative bg-gradient-to-br from-indigo-500 via-indigo-600 to-purple-600 px-6 py-8">
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '22px 22px',
        }} />
        <div className="relative flex flex-wrap items-start justify-between gap-4">
          <div className="flex min-w-0 items-start gap-4">
            {coverImage && (
              <div className="hidden h-20 w-20 flex-shrink-0 overflow-hidden rounded-xl bg-white/10 ring-1 ring-white/30 sm:block">
                <img
                  src={coverImage}
                  alt={name}
                  className="h-full w-full object-cover"
                  onError={(ev) => {
                    (ev.currentTarget as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
            )}
            <div className="min-w-0">
              <div className="mb-2 flex items-center gap-3">
                <StatusPill status={status} />
                {isDeleted && (
                  <span className="rounded-full bg-white/20 px-2.5 py-1 text-xs font-medium text-white">
                    Soft-deleted
                  </span>
                )}
              </div>
              <h1 className="truncate text-2xl font-bold text-white">{name}</h1>
              <p className="mt-1 text-sm text-indigo-100">
                {e.EntityType?.name_friendly || e.type_name || `Type ${e.etid ?? '—'}`}
              </p>
            </div>
          </div>
          {primaryPrice != null && (
            <div className="rounded-xl bg-white/15 px-4 py-2 text-right backdrop-blur">
              <p className="text-xs text-indigo-100">Price</p>
              <p className="text-2xl font-bold text-white">{money(primaryPrice)}</p>
            </div>
          )}
        </div>
      </div>

      {/* Meta strip */}
      <div className="grid grid-cols-2 gap-4 px-6 py-5 sm:grid-cols-3 lg:grid-cols-5">
        <MetaItem icon={Hash} label="EID" value={<span className="font-mono">{e.eid ?? eid}</span>} />
        <MetaItem icon={Hash} label="FFID" value={<span className="font-mono text-xs">{e.ffid ?? '—'}</span>} />
        <MetaItem icon={User} label="Owner" value={<OwnerValue owner={e.owner} oid={e.oid} uid={e.uid} />} />
        <MetaItem icon={Calendar} label="Created" value={fmtDate(e.date_added)} />
        <MetaItem icon={Clock} label="Updated" value={fmtDate(e.date_modified || e.date_status_changed)} />
      </div>
    </div>
  </>
);
